import React from "react";
import { Card, Table, Badge, Spinner, Dropdown } from "react-bootstrap";
import {
  // IconShoppingCart,
  IconEdit,
  IconTrash,
  IconHistory,
  IconDots,
  IconPackage,
  IconSortAscending,
  IconSortDescending,
  IconX,
  IconCheck,
  IconArrowRight,
} from "@tabler/icons-react";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import { formatPrice } from "utils/utils";

// const getStockStatus = (current, minimum) => {
//   if (current === 0) {
//     return {
//       status: "empty",
//       color: "#dc2626",
//       bgColor: "#fef2f2",
//       label: "Habis",
//     };
//   } else if (current <= minimum) {
//     return {
//       status: "low",
//       color: "#d97706",
//       bgColor: "#fffbeb",
//       label: "Stok Rendah",
//     };
//   } else {
//     return {
//       status: "good",
//       color: "#059669",
//       bgColor: "#ecfdf5",
//       label: "Stok Baik",
//     };
//   }
// };

// We'll derive warehouse columns dynamically from items below. The per-warehouse
// compact display component was removed in favor of dedicated columns.

const InventoryTable = ({
  inventory,
  filteredItems,
  getCategoryData,
  onEditItem,
  onDeleteItem,
  onToggleActive,
  onUpdateStock,
  onShowItemHistory,
  onTransferStock,
  onImageClick,
  onUpdateStockManually,
  Pagination,
  filterOptions,
  handleSortFilter,
}) => {
  // derive the unique warehouses from the current items so we can render
  // one column per warehouse (keeps column order stable across rows)
  const warehouses = React.useMemo(() => {
    const map = new Map();
    const source =
      filteredItems && filteredItems.length
        ? filteredItems
        : inventory?.data || [];
    source.forEach((item) => {
      (item.warehouse_stocks || []).forEach((ws) => {
        const key = ws.warehouse_id ?? ws.warehouse_code ?? ws.warehouse_name;
        if (!map.has(key)) {
          map.set(key, {
            warehouse_id: ws.warehouse_id,
            warehouse_code: ws.warehouse_code,
            warehouse_name: ws.warehouse_name,
          });
        }
      });
    });
    return Array.from(map.values());
  }, [filteredItems, inventory]);

  const totalCols = 3 + 1 + warehouses.length + 5; // image, item, category, [price], warehouses..., total, actions

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = filterOptions?.orderBy === field;
    const isAsc = filterOptions?.sort === "asc";

    return (
      <th
        className={className}
        onClick={() => {
          if (!handleSortFilter) return;
          if (isSorted) {
            handleSortFilter(field, isAsc ? "desc" : "asc");
          } else {
            handleSortFilter(field, "asc"); // Default to asc when changing columns
          }
        }}
        style={{ cursor: "pointer", userSelect: "none" }}
        title={`Sort by ${label}`}
      >
        <div className={`d-flex align-items-center gap-1 ${className.includes('text-center') ? 'justify-content-center' : 'justify-content-between'}`}>
          <span>{label}</span>
          {isSorted ? (
            isAsc ? <IconSortAscending size={14} className="text-primary" /> : <IconSortDescending size={14} className="text-primary" />
          ) : (
            <IconSortDescending size={14} className="text-muted opacity-25" />
          )}
        </div>
      </th>
    );
  };

  return (
    <Card>
      <Card.Body style={{ padding: 0 }}>
        <div className="table-responsive">
          <Table
            // striped
            bordered
            hover
            className="table-vcenter"
            style={{ marginBottom: 0 }}
          >
            <thead>
              <tr>
                <th style={{ width: "80px", textAlign: "center" }}>Image</th>
                {renderSortableHeader("Item", "name")}
                {renderSortableHeader("Category", "category_id")}
                <PermissionMiddleware allowedPermissions={["see_price_inventory"]}>
                  {renderSortableHeader("Price", "price")}
                </PermissionMiddleware>
                {warehouses.map((w) => (
                  <th
                    key={w.warehouse_id || w.warehouse_code}
                    className="text-center"
                  >
                    {w.warehouse_name || w.warehouse_code}
                  </th>
                ))}
                {/* {renderSortableHeader("Total Stock", "current_stock_total", "text-center")} */}
                <th style={{ textAlign: "center" }}>Total Stock</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventory.loading ? (
                <tr>
                  <td
                    colSpan={totalCols}
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    <Spinner animation="border" />
                    <div style={{ marginTop: "8px" }}>Loading inventory...</div>
                  </td>
                </tr>
              ) : filteredItems.length > 0 ? (
                filteredItems.map((item) => {
                  // const stockStatus = getStockStatus(
                  //   item.current_stock_total || 0,
                  //   item.minimum_stock
                  // );
                  // const category = getCategoryData(item.category_id);

                  return (
                    <tr key={item.id}>
                      <td style={{ textAlign: "center", width: "80px" }}>
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              cursor: "pointer",
                              border: "1px solid #e5e7eb"
                            }}
                            onClick={() => onImageClick && onImageClick(item)}
                          />
                        ) : (
                          <div
                            style={{
                              width: "50px",
                              height: "50px",
                              backgroundColor: "#f3f4f6",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#9ca3af",
                              fontSize: "20px",
                              border: "1px solid #e5e7eb",
                              cursor: "pointer"
                            }}
                            onClick={() => onImageClick && onImageClick(item)}
                            title="Click to upload image"
                          >
                            📷
                          </div>
                        )}
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: "500", color: "#1f2937" }}>
                            {item.name}
                            <small className="text-muted"> ({item.unit})</small>
                          </div>
                          {item.nama_alias && (
                            <div className="text-primary small fw-bold">
                              Alias: {item.nama_alias}
                            </div>
                          )}
                          {item.description && (
                            <small className="text-muted">
                              {item.description.length > 50
                                ? `${item.description.substring(0, 50)}...`
                                : item.description}
                            </small>
                          )}
                          <div className="mt-1">
                            {item.is_active === false ? (
                              <Badge bg="danger" text="white">Tidak Aktif</Badge>
                            ) : (
                              <Badge bg="success" text="white">Aktif</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge
                          style={{
                            backgroundColor: item.category?.color || "#6b7280",
                            color: "white",
                          }}
                        >
                          {item.category?.name || "Uncategorized"}
                        </Badge>
                      </td>
                      <PermissionMiddleware allowedPermissions={["see_price_inventory"]}>
                        <td className="text-end text-success">
                          {formatPrice(item.price || 0)}
                        </td>
                      </PermissionMiddleware>
                      {warehouses.map((w) => {
                        const ws = (item.warehouse_stocks || []).find(
                          (s) =>
                            (s.warehouse_id ??
                              s.warehouse_code ??
                              s.warehouse_name) ===
                            (w.warehouse_id ??
                              w.warehouse_code ??
                              w.warehouse_name)
                        );
                        const stock = ws ? ws.stock ?? 0 : null;
                        const minStock = ws?.minimum_stock;
                        const isOutOfStock = stock === 0;
                        const isLowStock =
                          stock !== null &&
                          minStock !== undefined &&
                          stock <= minStock &&
                          stock > 0;

                        return (
                          <td
                            key={w.warehouse_id || w.warehouse_code}
                            style={{
                              textAlign: "center",
                              fontWeight: "500",
                              backgroundColor: isOutOfStock
                                ? "#dc2626" // Red for out of stock
                                : isLowStock
                                  ? "#d97706" // Orange for low stock
                                  : "transparent",
                            }}
                          >
                            {stock !== null ? (
                              <div>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      isOutOfStock || isLowStock
                                        ? "#ffffff"
                                        : "#1f2937",
                                  }}
                                >
                                  {stock}
                                </span>
                                <small
                                  style={{
                                    color:
                                      isOutOfStock || isLowStock
                                        ? "#ffffff"
                                        : "#1f2937",
                                  }}
                                >
                                  {" "}
                                  {item.unit}
                                </small>
                                {minStock !== undefined && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color:
                                        isOutOfStock || isLowStock
                                          ? "#ffffff"
                                          : "#1f2937",
                                    }}
                                  >
                                    Min: {minStock}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: "center", fontWeight: "500" }}>
                        {item.current_stock_total || 0}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            size="sm"
                            id={`dropdown-${item.id}`}
                          >
                            <IconDots size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => onShowItemHistory(item)}
                            >
                              <IconHistory size={16} className="me-2" />
                              Item History
                            </Dropdown.Item>
                            {/* <Dropdown.Item onClick={() => onUpdateStock(item)}>
                              <IconShoppingCart size={16} className="me-2" />
                              Update Stock
                            </Dropdown.Item> */}
                            <Dropdown.Item
                              onClick={() => onTransferStock(item)}
                            >
                              <IconArrowRight size={16} className="me-2" />
                              Transfer Stock
                            </Dropdown.Item>
                            <PermissionMiddleware allowedPermissions={["edit_inventory"]}>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => onEditItem(item)}>
                                <IconEdit size={16} className="me-2" />
                                Edit Item
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => onToggleActive && onToggleActive(item)}
                                className={item.is_active === false ? "text-success" : "text-warning"}
                              >
                                {item.is_active === false ? (
                                  <>
                                    <IconCheck size={16} className="me-2" />
                                    Activate Item
                                  </>
                                ) : (
                                  <>
                                    <IconX size={16} className="me-2" />
                                    Deactivate Item
                                  </>
                                )}
                              </Dropdown.Item>
                            </PermissionMiddleware>

                            <Dropdown.Item
                              onClick={() => {
                                if (onUpdateStockManually) {
                                  onUpdateStockManually(item);
                                }
                              }}
                              className="text-primary"
                            >
                              <IconPackage size={16} className="me-2" />
                              Update stock manually
                            </Dropdown.Item>
                            <PermissionMiddleware allowedRoles={["superadmin"]}>
                              <Dropdown.Item
                                onClick={() => onDeleteItem(item)}
                                className="text-danger"
                              >
                                <IconTrash size={16} className="me-2" />
                                Delete Item
                              </Dropdown.Item>
                            </PermissionMiddleware>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={totalCols}
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    No inventory items found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>

      {/* Pagination */}
      {Pagination && (
        <div style={{ padding: "16px", borderTop: "1px solid #e5e7eb" }}>
          {Pagination}
        </div>
      )}
    </Card>
  );
};

export default InventoryTable;
